import { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import axios from 'axios'
import moment from 'moment'

import { URL as BACKEND_URL, fetchNewToken } from '../../../gql/client'
import { STORAGE_NAMES } from '../../../helpers/consts'
import { parseId } from '../../../helpers'

interface VicidialCampaign {
  campaign_id: string
  campaign_name: string
  active: string
  dial_method: string
}

interface PushResult {
  lead_id: string
  lead_name?: string
  status: 'added' | 'failed' | 'skipped'
  reason?: string
}

interface StageOption {
  id: string
  name: string
}

interface ModalPushToVicidialProps {
  isOpen: boolean
  onClose: () => void
  stages: StageOption[]
  info: {
    pipelineId: string
    pipelineName: string
    statePipeline: string
    stateNamePipeline: string
    searchName: string
    searchPhone: string
    dateFrom: Date | null
    dateTo: Date | null
    userId: string
    userName: string
    totalLeads: number
    leadsSelected?: { id: string; name: string }[]
  }
}

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem(STORAGE_NAMES.TOKEN) || ''}`,
})

/** Axios wrapper that retries once with a refreshed JWT on 401. */
const authPost = async (url: string, data: any) => {
  try {
    return await axios.post(url, data, { headers: getAuthHeaders() })
  } catch (err: any) {
    if (err?.response?.status === 401) {
      const newToken = await fetchNewToken()
      if (newToken) {
        return await axios.post(url, data, {
          headers: { Authorization: `Bearer ${newToken}` },
        })
      }
    }
    throw err
  }
}

export const ModalPushToVicidial = ({ isOpen, onClose, info, stages }: ModalPushToVicidialProps) => {
  const [campaigns, setCampaigns] = useState<VicidialCampaign[]>([])
  const [loadingCampaigns, setLoadingCampaigns] = useState(false)
  const [selectedCampaignId, setSelectedCampaignId] = useState('')
  const [selectedStageIds, setSelectedStageIds] = useState<string[]>([])
  const [pushing, setPushing] = useState(false)
  const [pushResults, setPushResults] = useState<{
    total: number
    added: number
    failed: number
    skipped: number
    results: PushResult[]
  } | null>(null)

  const fetchCampaigns = useCallback(async () => {
    setLoadingCampaigns(true)
    try {
      const response = await authPost(
        `${BACKEND_URL}/api/telephony/vicidial/campaigns`,
        {}
      )
      setCampaigns(response.data.campaigns || [])
    } catch {
      toast.error('Failed to load PolicyDial campaigns')
    } finally {
      setLoadingCampaigns(false)
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      fetchCampaigns()
      setPushResults(null)
      setSelectedCampaignId('')
      // Pre-select current stage filter if active
      setSelectedStageIds(info.statePipeline ? [info.statePipeline] : [])
    }
  }, [isOpen, fetchCampaigns, info.statePipeline])

  const selectedCampaign = campaigns.find((c) => c.campaign_id === selectedCampaignId)

  const hasSelectedLeads = info.leadsSelected && info.leadsSelected.length > 0
  const leadCount = hasSelectedLeads ? info.leadsSelected!.length : (info.totalLeads || 0)
  const hasFilters = !!(selectedStageIds.length > 0 || info.dateFrom || info.dateTo || info.userId || info.searchName || info.searchPhone)

  const toggleStage = (stageId: string) => {
    setSelectedStageIds((prev) =>
      prev.includes(stageId) ? prev.filter((id) => id !== stageId) : [...prev, stageId]
    )
  }

  const selectAllStages = () => {
    setSelectedStageIds(stages.map((s) => s.id))
  }

  const clearAllStages = () => {
    setSelectedStageIds([])
  }

  const handlePush = async () => {
    if (!selectedCampaignId) return
    setPushing(true)
    setPushResults(null)

    try {
      let response
      if (hasSelectedLeads) {
        // Push specific selected leads
        response = await authPost(
          `${BACKEND_URL}/api/telephony/vicidial/campaigns/push-leads`,
          {
            campaign_id: selectedCampaignId,
            campaign_name: selectedCampaign?.campaign_name || '',
            lead_ids: info.leadsSelected!.map((l) => parseId(l.id)),
          }
        )
      } else {
        // Push by filters
        response = await authPost(
          `${BACKEND_URL}/api/telephony/vicidial/campaigns/push-filtered-leads`,
          {
            campaign_id: selectedCampaignId,
            campaign_name: selectedCampaign?.campaign_name || '',
            pipeline_id: info.pipelineId ? parseId(info.pipelineId) : undefined,
            stage_pipeline_ids: selectedStageIds.length > 0 ? selectedStageIds.map((id) => parseId(id)) : undefined,
            date_from: info.dateFrom ? moment(info.dateFrom).format('YYYY-MM-DD') : undefined,
            date_to: info.dateTo ? moment(info.dateTo).format('YYYY-MM-DD') : undefined,
            user_id: info.userId ? parseId(info.userId) : undefined,
            search_name: info.searchName || undefined,
            search_phone: info.searchPhone || undefined,
          }
        )
      }

      const data = response.data
      setPushResults(data)

      if (data.added > 0) toast.success(`${data.added} lead(s) added to PolicyDial`)
      if (data.failed > 0) toast.warning(`${data.failed} lead(s) failed`)
      if (data.skipped > 0) toast.info(`${data.skipped} lead(s) skipped`)
    } catch (err: any) {
      const msg = err?.response?.data?.error || err.message
      toast.error(`Error: ${msg}`)
    } finally {
      setPushing(false)
    }
  }

  const handleClose = () => {
    setPushResults(null)
    onClose()
  }

  return (
    <Modal show={isOpen} onHide={handleClose} centered size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className='bi bi-telephone-outbound me-2 text-primary'></i>
          {pushResults ? 'Push Results' : 'Push Leads to PolicyDial Campaign'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {pushResults ? (
          <div>
            <div className='d-flex gap-3 mb-4'>
              <div className='bg-light rounded p-3 text-center flex-fill'>
                <div className='fs-2 fw-bold text-success'>{pushResults.added}</div>
                <small className='text-muted'>Added</small>
              </div>
              <div className='bg-light rounded p-3 text-center flex-fill'>
                <div className='fs-2 fw-bold text-danger'>{pushResults.failed}</div>
                <small className='text-muted'>Failed</small>
              </div>
              <div className='bg-light rounded p-3 text-center flex-fill'>
                <div className='fs-2 fw-bold text-secondary'>{pushResults.skipped}</div>
                <small className='text-muted'>Skipped</small>
              </div>
            </div>
            {pushResults.results.filter((r) => r.status !== 'added').length > 0 && (
              <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                <small className='text-muted fw-bold d-block mb-2'>Issues:</small>
                {pushResults.results
                  .filter((r) => r.status !== 'added')
                  .map((r, i) => (
                    <div key={i} className='d-flex justify-content-between align-items-center border-bottom py-2'>
                      <span className='small'>{r.lead_name || r.lead_id}</span>
                      <Badge bg={r.status === 'failed' ? 'danger' : 'secondary'} className='small'>
                        {r.reason || r.status}
                      </Badge>
                    </div>
                  ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            {/* Stage selector */}
            {!hasSelectedLeads && stages.length > 0 && (
              <div className='mb-4'>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <Form.Label className='fw-bold m-0'>
                    <i className='bi bi-diagram-3 me-1'></i>
                    Select Stages
                  </Form.Label>
                  <div className='d-flex gap-2'>
                    <Button variant='link' size='sm' className='p-0 small' onClick={selectAllStages}>
                      Select All
                    </Button>
                    <span className='text-muted'>|</span>
                    <Button variant='link' size='sm' className='p-0 small text-danger' onClick={clearAllStages}>
                      Clear
                    </Button>
                  </div>
                </div>
                <div className='d-flex flex-wrap gap-2'>
                  {stages.map((stage) => {
                    const isSelected = selectedStageIds.includes(stage.id)
                    return (
                      <Badge
                        key={stage.id}
                        bg={isSelected ? 'primary' : 'light'}
                        text={isSelected ? 'white' : 'dark'}
                        className={`px-3 py-2 border ${isSelected ? 'border-primary' : ''}`}
                        style={{ cursor: 'pointer', fontSize: '0.85rem' }}
                        onClick={() => toggleStage(stage.id)}
                      >
                        {isSelected && <i className='bi bi-check-circle-fill me-1'></i>}
                        {!isSelected && <i className='bi bi-circle me-1'></i>}
                        {stage.name}
                      </Badge>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Other active filters */}
            <div className='bg-light rounded p-3 mb-4'>
              <small className='text-muted fw-bold text-uppercase d-block mb-2'>Active Filters</small>
              <div className='d-flex flex-wrap gap-2'>
                {info.pipelineName && (
                  <Badge bg='light' text='dark' className='px-2 py-1 border'>
                    <i className='bi bi-funnel me-1 text-primary'></i>{info.pipelineName}
                  </Badge>
                )}
                {selectedStageIds.length > 0 && (
                  <Badge bg='light' text='dark' className='px-2 py-1 border'>
                    <i className='bi bi-diagram-3 me-1 text-info'></i>
                    {selectedStageIds.length} stage{selectedStageIds.length > 1 ? 's' : ''} selected
                  </Badge>
                )}
                {info.userName && (
                  <Badge bg='light' text='dark' className='px-2 py-1 border'>
                    <i className='bi bi-person me-1 text-secondary'></i>{info.userName}
                  </Badge>
                )}
                {info.dateFrom && (
                  <Badge bg='light' text='dark' className='px-2 py-1 border'>
                    <i className='bi bi-calendar me-1 text-success'></i>
                    From: {moment(info.dateFrom).format('MMM DD, YYYY')}
                  </Badge>
                )}
                {info.dateTo && (
                  <Badge bg='light' text='dark' className='px-2 py-1 border'>
                    <i className='bi bi-calendar me-1 text-success'></i>
                    To: {moment(info.dateTo).format('MMM DD, YYYY')}
                  </Badge>
                )}
                {info.searchName && (
                  <Badge bg='light' text='dark' className='px-2 py-1 border'>
                    <i className='bi bi-search me-1 text-warning'></i>
                    Name: {info.searchName}
                  </Badge>
                )}
                {info.searchPhone && (
                  <Badge bg='light' text='dark' className='px-2 py-1 border'>
                    <i className='bi bi-telephone me-1 text-warning'></i>
                    Phone: {info.searchPhone}
                  </Badge>
                )}
                {!hasFilters && !info.pipelineName && (
                  <span className='text-muted small'>No filters applied — all leads in pipeline will be pushed</span>
                )}
              </div>
              <div className='mt-3'>
                {hasSelectedLeads ? (
                  <>
                    <span className='fs-5 fw-bold text-primary'>{leadCount.toLocaleString()}</span>
                    <span className='text-muted ms-1'>leads selected manually</span>
                  </>
                ) : (
                  <span className='text-muted'>
                    <i className='bi bi-funnel me-1'></i>
                    All leads matching these filters will be pushed
                  </span>
                )}
              </div>
            </div>

            {/* Campaign selector */}
            <Form.Group className='mb-3'>
              <Form.Label className='fw-bold'>
                <i className='bi bi-telephone-outbound me-1'></i>
                Select PolicyDial Campaign
              </Form.Label>
              {loadingCampaigns ? (
                <div className='text-center p-3'>
                  <Spinner animation='border' size='sm' />
                  <span className='ms-2 text-muted small'>Loading campaigns...</span>
                </div>
              ) : campaigns.length === 0 ? (
                <Alert variant='warning' className='small'>
                  No PolicyDial campaigns found. Check your telephony configuration.
                </Alert>
              ) : (
                <Form.Select
                  value={selectedCampaignId}
                  onChange={(e) => setSelectedCampaignId(e.target.value)}
                >
                  <option value=''>-- Select a campaign --</option>
                  {campaigns.map((c) => (
                    <option key={c.campaign_id} value={c.campaign_id}>
                      {c.campaign_name || c.campaign_id}
                      {c.active === 'Y' ? ' (Active)' : ' (Inactive)'}
                      {c.dial_method ? ` - ${c.dial_method}` : ''}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Form.Group>

            {selectedCampaign && (
              <Alert variant='info' className='small'>
                <i className='bi bi-info-circle me-1'></i>
                {hasSelectedLeads
                  ? <><strong>{leadCount.toLocaleString()}</strong> lead(s) will be added to </>
                  : <>Leads matching your filters will be added to </>
                }
                <strong>{selectedCampaign.campaign_name}</strong> and dialed automatically
                by PolicyDial's auto-dialer.
              </Alert>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {pushResults ? (
          <Button variant='secondary' onClick={handleClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant='secondary' onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant='primary'
              onClick={handlePush}
              disabled={!selectedCampaignId || pushing || (hasSelectedLeads && leadCount === 0) || (!hasSelectedLeads && !hasFilters)}
            >
              {pushing ? (
                <>
                  <Spinner animation='border' size='sm' className='me-1' />
                  Pushing leads...
                </>
              ) : (
                <>
                  <i className='bi bi-send me-1'></i>
                  {hasSelectedLeads
                    ? `Push ${leadCount.toLocaleString()} Leads to PolicyDial`
                    : 'Push Filtered Leads to PolicyDial'}
                </>
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  )
}
